"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { API_BASE } from "@/lib/auth";

const SESSION_KEY = "cmthub_session_id";
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 15;
const MAX_QUEUE_CAP = 100;

interface TrackedEvent {
  type: string;
  data: Record<string, unknown>;
  timestamp: number;
}

// Module-level event queue buffer so events persist across React re-renders
let eventQueue: TrackedEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

/**
 * Flush all buffered events in a single batched HTTP request or beacon.
 */
function flushEvents(isUnloading = false): void {
  if (typeof window === "undefined" || eventQueue.length === 0) return;

  const eventsToSend = eventQueue.splice(0, eventQueue.length);
  const url = `${API_BASE}/api/v1/insights/batch`;
  const payload = JSON.stringify({ events: eventsToSend });

  if (isUnloading && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) return;
    } catch {
      /* fallback to fetch below if sendBeacon throws */
    }
  }

  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // auth cookie sent automatically
      body: payload,
      keepalive: true,
    }).catch(() => {
      // If network failed and not unloading, restore events to front of queue (up to cap)
      if (!isUnloading && eventQueue.length < MAX_QUEUE_CAP) {
        eventQueue.unshift(...eventsToSend.slice(0, MAX_QUEUE_CAP - eventQueue.length));
      }
    });
  } catch {
    /* swallow */
  }
}

/**
 * Enqueue a tracked event into the client-side buffer.
 */
function enqueueEvent(type: string, data: Record<string, unknown>, immediate = false): void {
  if (typeof window === "undefined") return;

  if (eventQueue.length >= MAX_QUEUE_CAP) {
    eventQueue.shift(); // Drop oldest event if capacity reached
  }

  eventQueue.push({
    type,
    data,
    timestamp: Date.now(),
  });

  if (immediate || eventQueue.length >= MAX_BATCH_SIZE) {
    flushEvents(false);
  }
}

// Public helper — callable from any component for ad-hoc events (search, filter, etc.)
export function trackInteraction(kind: string, payload: Record<string, unknown> = {}) {
  const isUrgent = kind === "js_error";
  enqueueEvent(
    "interaction",
    {
      kind,
      path: typeof window !== "undefined" ? window.location.pathname : null,
      session_id: getSessionId(),
      payload,
    },
    isUrgent
  );
}

const INTERACTIVE_SELECTOR =
  '[data-track], button, a[href], [role="button"], [role="tab"], [role="menuitem"], [role="link"], summary';

/** Derive a stable-ish track key + label for an element that has no data-track. */
function deriveClickKey(el: HTMLElement): { key: string; label: string } | null {
  const explicit = el.getAttribute("data-track");
  const label = (el.innerText || el.getAttribute("aria-label") || el.getAttribute("title") || "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 80);
  if (explicit) return { key: explicit, label };

  const tag = el.tagName.toLowerCase();
  const base = el.id || label || el.getAttribute("href") || "";
  const slug = base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  if (!slug) return null;
  return { key: `${tag}:${slug}`, label };
}

/**
 * Mount once near the top of the authenticated tree. Tracks:
 *  - page views (incl. time-on-page + max scroll depth for the previous page)
 *  - clicks on ALL interactive elements (buttons, links, tabs); explicit
 *    data-track="..." keys are preserved so funnels keep working
 *  - form abandonment (form opened via data-track-form="..." but no matching submit click within session)
 *  - uncaught JS errors + unhandled promise rejections (kind="js_error")
 *
 * All events are batched client-side and flushed every 5s, on 15-event threshold,
 * or on page exit/visibility change via sendBeacon.
 */
/**
 * The page as people experience it. /staff and /dashboard switch sections
 * with history.pushState, so the pathname alone logs every section as one
 * page; an allow-listed `section` and `tab` are kept so Platform Insights can
 * say "Staff portal · Events Pipeline". Nothing else from the query string is
 * recorded (it can carry ids, filters or tokens).
 */
const TRACKED_PARAMS = ["section", "tab"] as const;

export function logicalPath(pathname: string, search: URLSearchParams | null): string {
  if (!search) return pathname;
  const kept = new URLSearchParams();
  for (const key of TRACKED_PARAMS) {
    const v = search.get(key);
    if (v && /^[a-z0-9_-]{1,40}$/i.test(v)) kept.set(key, v);
  }
  const q = kept.toString();
  return q ? `${pathname}?${q}` : pathname;
}

export function useActivityTracking() {
  const rawPathname = usePathname();
  const searchParams = useSearchParams();
  const pathname = rawPathname ? logicalPath(rawPathname, searchParams) : rawPathname;
  const prevPathRef = useRef<string | null>(null);
  const enteredAtRef = useRef<number>(Date.now());
  const maxScrollRef = useRef<number>(0);

  // Periodic batch flusher (5 seconds) + lifecycle listeners
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!flushTimer) {
      flushTimer = setInterval(() => {
        flushEvents(false);
      }, FLUSH_INTERVAL_MS);
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushEvents(true);
      }
    };

    const onPageHide = () => {
      flushEvents(true);
    };

    const onBeforeUnload = () => {
      flushEvents(true);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  // Page-view tracking with time-on-page + scroll depth for the previous page
  useEffect(() => {
    if (typeof window === "undefined" || !pathname) return;

    // Flush the previous page's stats before logging the new view
    const prev = prevPathRef.current;
    if (prev && prev !== pathname) {
      enqueueEvent("page-view", {
        path: prev,
        referrer: document.referrer || null,
        session_id: getSessionId(),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        duration_ms: Date.now() - enteredAtRef.current,
        max_scroll_pct: maxScrollRef.current,
      });
    } else {
      // first mount — just log entry
      enqueueEvent("page-view", {
        path: pathname,
        referrer: document.referrer || null,
        session_id: getSessionId(),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
    }
    prevPathRef.current = pathname;
    enteredAtRef.current = Date.now();
    maxScrollRef.current = 0;
  }, [pathname]);

  // Scroll-depth tracker — maintain max scroll % seen on current page
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;
      if (scrollable <= 0) return;
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      if (pct > maxScrollRef.current) maxScrollRef.current = pct;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Flush time-on-page + scroll on tab close
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onBeforeUnload = () => {
      const path = prevPathRef.current;
      if (!path) return;
      enqueueEvent("page-view", {
        path,
        referrer: document.referrer || null,
        session_id: getSessionId(),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        duration_ms: Date.now() - enteredAtRef.current,
        max_scroll_pct: maxScrollRef.current,
      });
      flushEvents(true);
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // data-track click handler (also handles form-open / form-submit pairing for abandonment)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const openedForms = new Map<string, number>(); // form key → opened-at ts

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest<HTMLElement>(INTERACTIVE_SELECTOR);
      if (!el) return;

      const derived = deriveClickKey(el);
      if (derived) {
        const explicit = el.hasAttribute("data-track");
        enqueueEvent("click", {
          path: window.location.pathname,
          element_id: el.id || null,
          element_label: derived.label.slice(0, 200),
          track_key: derived.key,
          extra: explicit ? null : { auto: true, tag: el.tagName.toLowerCase() },
        });
      }

      // Pair form-open / form-submit for abandonment detection. These attrs
      // live on the explicitly tagged element, so read from the data-track node.
      const dt = target.closest<HTMLElement>("[data-track]");
      const formOpen = dt?.getAttribute("data-track-form-open");
      const formSubmit = dt?.getAttribute("data-track-form-submit");
      if (formOpen) openedForms.set(formOpen, Date.now());
      if (formSubmit && openedForms.has(formSubmit)) {
        const openedAt = openedForms.get(formSubmit)!;
        openedForms.delete(formSubmit);
        trackInteraction("form_completed", {
          form: formSubmit,
          time_to_complete_ms: Date.now() - openedAt,
        });
      }
    };

    // Page exit: any form that was opened but never submitted = abandoned
    const onLeave = () => {
      openedForms.forEach((openedAt, form) => {
        trackInteraction("form_abandoned", {
          form,
          time_open_ms: Date.now() - openedAt,
        });
      });
      openedForms.clear();
      flushEvents(true);
    };

    window.addEventListener("click", onClick, true);
    window.addEventListener("beforeunload", onLeave);
    return () => {
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("beforeunload", onLeave);
    };
  }, []);

  // Uncaught JS errors + unhandled promise rejections → kind="js_error".
  // Deduped per signature and capped per session so a tight error loop can't
  // flood the ingest endpoint.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const seen = new Set<string>();
    let sent = 0;
    const CAP = 30;

    const report = (message: string, source?: string, line?: number, stack?: string) => {
      const sig = `${message}@${source || ""}:${line || ""}`;
      if (seen.has(sig) || sent >= CAP) return;
      seen.add(sig);
      sent += 1;
      trackInteraction("js_error", {
        message: (message || "").slice(0, 500),
        source: (source || "").slice(0, 300) || null,
        line: line ?? null,
        stack: (stack || "").slice(0, 1000) || null,
        user_agent: navigator.userAgent.slice(0, 300),
      });
    };

    const onError = (e: ErrorEvent) =>
      report(e.message, e.filename, e.lineno, e.error?.stack);
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const msg = reason?.message || String(reason ?? "Unhandled rejection");
      report(`Unhandled rejection: ${msg}`, undefined, undefined, reason?.stack);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
}
